import { parseSpreadsheetBuffer } from './spreadsheetCompanyImport';
import { resolveSupplierTickerFromRow } from './supplierTickerUtils';

export const HEADER_ORIENTATION = {
  ROW: 'row',
  COLUMN: 'column',
};

export const IMPORT_DTO_FIELDS = {
  tenantCompany: [
    'name',
    'legal_name',
    'duns_number',
    'country',
    'city',
    'industry',
    'annual_revenue',
    'employee_count',
    'stock_ticker',
  ],
  facilities: [
    'facility_id',
    'facility_name',
    'facility_type',
    'address',
    'city',
    'country',
    'latitude',
    'longitude',
  ],
  suppliers: [
    'ticker',
    'name',
    'description',
    'industry',
    'tier',
    'legal_name',
    'duns_number',
    'country',
    'region',
    'categories',
    'criticality_score',
    'is_sole_source',
    'is_strategic',
  ],
  supplierFacilities: [
    'address_id',
    'ticker',
    'facility_id',
    'facility_name',
    'street_address',
    'address',
    'city',
    'state',
    'region',
    'postal_code',
    'country',
    'location',
    'latitude',
    'longitude',
    'address_type',
    'facility_type',
    'is_primary',
    'capacity_units',
    'capacity_value',
    'capacity_share',
    'operational_status',
    'headcount',
    'hs_codes',
    'since',
    'certifications',
    'uflpa_territory',
    'export_control_zone',
    'last_audit_date',
    'audit_outcome',
    'insurance_coverage',
    'natural_hazard_score',
    'single_site_risk',
    'lead_time_days',
    'primary_port',
    'freight_mode',
    'avg_transit_days',
    'annual_spend_share',
    'last_verified_at',
    'verified_by',
    'data_source',
    'confidence_rating',
  ],
  commodities: ['commodity', 'category', 'description', 'unit'],
  geographies: ['country', 'region', 'city', 'risk_level'],
  logistics: ['route_name', 'mode', 'origin', 'destination', 'lead_time_days', 'cost'],
  products: ['product_name', 'sku', 'category', 'description'],
  graphOverview: ['source', 'target', 'relationship_type', 'weight', 'notes'],
};

const normalizeHeader = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Extra header aliases per import type (NatureCrunch matrix CSV uses `id` as ticker). */
const IMPORT_FIELD_ALIASES = {
  suppliers: {
    ticker: ['id', 'supplierid', 'supplier_id', 'suppliercode', 'companyid'],
    legal_name: ['legalname'],
    is_sole_source: ['issolesource'],
    is_strategic: ['isstrategic'],
    criticality_score: ['criticalityscore'],
    annual_spend: ['annualspend'],
    duns_number: ['dunsnumber'],
    uflpa_flag: ['uflpaflag'],
    data_source: ['datasource'],
    confidence_rating: ['confidencerating'],
  },
  supplierFacilities: {
    address_id: ['id', 'addressid', 'address_id', 'sfacid'],
    ticker: ['supplierid', 'supplier_id', 'supplierticker'],
    facility_id: ['facilityid', 'facility_id'],
    facility_name: ['name', 'facilityname'],
    street_address: ['streetaddress'],
    is_primary: ['isprimary'],
    facility_type: ['facilitytype'],
    data_source: ['datasource'],
    confidence_rating: ['confidencerating'],
    uflpa_territory: ['uflpaterritory'],
    export_control_zone: ['exportcontrolzone'],
    last_audit_date: ['lastauditdate'],
    audit_outcome: ['auditoutcome'],
    insurance_coverage: ['insurancecoverage'],
    natural_hazard_score: ['naturalhazardscore'],
    single_site_risk: ['singlesiterisk'],
    lead_time_days: ['leadtimedays'],
    primary_port: ['primaryport'],
    freight_mode: ['freightmode'],
    avg_transit_days: ['avgtransitdays'],
    annual_spend_share: ['annualspendshare'],
    last_verified_at: ['lastverifiedat'],
    verified_by: ['verifiedby'],
    capacity_units: ['capacityunits'],
    capacity_value: ['capacityvalue'],
    capacity_share: ['capacityshare'],
    operational_status: ['operationalstatus'],
    hs_codes: ['hscodes'],
  },
};

export const parseCsvDataForMapping = (buffer, orientation) =>
  parseSpreadsheetBuffer(buffer, orientation || HEADER_ORIENTATION.ROW);

export const guessDtoMapping = (headers, dtoFields, importKey) => {
  const normMap = new Map();
  headers.forEach((header) => {
    normMap.set(normalizeHeader(header), header);
  });

  const aliasesByField = IMPORT_FIELD_ALIASES[importKey] || {};
  const usedHeaders = new Set();

  const mapping = {};
  dtoFields.forEach((field) => {
    const camel = field.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    const candidates = [field, camel, ...(aliasesByField[field] || [])];
    const matchedHeader = candidates
      .map((candidate) => normMap.get(normalizeHeader(candidate)))
      .find((header) => header && !usedHeaders.has(header));
    mapping[field] = matchedHeader || null;
    if (matchedHeader) {
      usedHeaders.add(matchedHeader);
    }
  });
  return mapping;
};

export const buildMappedRows = (rows, mapping, dtoFields) => {
  const mapped = [];
  rows.forEach((row) => {
    const out = {};
    let any = false;
    dtoFields.forEach((field) => {
      const source = mapping[field];
      const value = source ? row[source] : '';
      const str = value == null ? '' : String(value).trim();
      out[field] = str;
      if (str) any = true;
    });
    if (!out.ticker) {
      const fallbackTicker = resolveSupplierTickerFromRow(row);
      if (fallbackTicker) out.ticker = fallbackTicker;
    }
    if (out.ticker && !out.supplier_id) {
      out.supplier_id = out.ticker;
    }
    if (any) {
      mapped.push(out);
    }
  });
  return mapped;
};

/** Returns true when every mapped row has a supplier ticker (from ticker or NatureCrunch id). */
export const mappedRowsHaveRequiredTicker = (rows) =>
  rows.length > 0 && rows.every((row) => resolveSupplierTickerFromRow(row).length > 0);

